import { useState } from 'react';
import type { ReactNode } from 'react';
import { useConnection, useRuntime } from './context';
import { useRemote } from './useRemote';
import { centsFromInput } from '../money/input';
import { formatMoney } from '../mirror/time';
import { Refund, RefundableSettlement } from '../wire';
import type { RefundTenderKind } from '../wire';
import { useLocale, useT } from '../messages';

/**
 * **Giving money back** (migration 0046, `docs/money-second-pass-design.md` §4).
 *
 * A refund is a **document, never an edit**. Spec §7.6's posture is *anonymize,
 * never falsify the ledger* and §7.4 rule 1 is *revenue on the date the money
 * arrived*, so this screen never touches the sale it is about: it writes a new
 * row, on today's date, naming what it reverses. The purchase on the client's
 * history stays exactly what it was, with a line underneath saying what came
 * back off it.
 *
 * ## It requires a connection, and says so plainly
 *
 * Every other write in this app is an intent in the outbox, because a checkout
 * in a basement is still a checkout (spec §8.2). This one is not, and the reason
 * is arithmetic rather than caution: the cap is `total − Σ prior refunds`, the
 * prior refunds are not in the mirror and never will be, and a device that
 * guessed would queue a document the ledger has to reject — *after* somebody at
 * the counter has told a guest she has her money back. So the sheet reads the
 * server's caps before it offers anything, and offline it prints one sentence
 * instead of a form.
 *
 * ## Nothing here processes anything
 *
 * Tapping "PayPal" records that the owner refunded in PayPal on her phone. The
 * platform has no rail, no live account, and no code path that would use one —
 * `routes/stays.ts` already says exactly this about a captured deposit, and
 * `docs/lovespa-discovery.md` §4 is why.
 */

/**
 * The eight ways money goes back.
 *
 * The six recorded external tenders, and two that keep it with the shop:
 * `credit_cash` puts real money on her account (the refund's negative revenue
 * and the credit's positive cancel out, because the shop still has it), and
 * `credit_gift` returns **goodwill** — §7.4 rule 2, which is why a gift-credit
 * refund must not come back as cash.
 */
const REFUND_TENDERS: readonly RefundTenderKind[] = [
  'cash',
  'check',
  'venmo',
  'paypal',
  'card',
  'other',
  'credit_cash',
  'credit_gift',
];

export function RefundSheet({
  settlementId,
  currency,
  onDone,
  onCancel,
}: {
  settlementId: string;
  currency: string;
  onDone: (refund: Refund) => void;
  onCancel: () => void;
}): ReactNode {
  const runtime = useRuntime();
  const t = useT();
  const locale = useLocale();
  const connection = useConnection();

  const sale = useRemote(
    (signal) =>
      runtime.api.request({
        method: 'GET',
        path: `/settlements/${settlementId}/refundable`,
        schema: RefundableSettlement,
        signal,
      }),
    [settlementId],
    // No poll: a cashier half-way through typing an amount must not have the
    // form move under their thumb. The write re-reads the cap under a row lock
    // anyway, so a stale number here can only ever be refused, never honoured.
    { pollMs: 0 },
  );

  const [amountText, setAmountText] = useState('');
  const [tipText, setTipText] = useState('');
  const [tender, setTender] = useState<RefundTenderKind>('cash');
  const [label, setLabel] = useState('');
  const [reason, setReason] = useState('');
  const [restock, setRestock] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const money = (cents: number): string => formatMoney(cents, currency, locale);
  const data = sale.data;

  /**
   * What the box holds, or — while it is empty — **everything that is left**.
   *
   * The common refund is the whole of what is still refundable, and making the
   * owner type a number she can already see would be the screen asking her to
   * transcribe it. An explicit amount is a partial.
   */
  const amountCents = centsFromInput(amountText) ?? data?.refundable_cents ?? 0;
  const tipCents = centsFromInput(tipText) ?? 0;

  const tooMuch =
    data !== null && (amountCents > data.refundable_cents || tipCents > data.refundable_tip_cents);

  async function submit(): Promise<void> {
    if (data === null) return;
    setBusy(true);
    setError(null);
    try {
      const refund = await runtime.api.request({
        method: 'POST',
        path: '/refunds',
        body: {
          settlement_id: data.settlement_id,
          amount_cents: amountCents,
          ...(tipCents === 0 ? {} : { tip_cents: tipCents }),
          // One tender, summing to the whole thing. A split refund is not a
          // screen anybody has asked for, and the wire takes a list, so it is
          // one field away the day somebody does.
          tenders: [
            {
              kind: tender,
              amount_cents: amountCents + tipCents,
              ...(tender === 'other' && label.trim() !== '' ? { label: label.trim() } : {}),
            },
          ],
          ...(reason.trim() === '' ? {} : { reason: reason.trim() }),
          // Per-line restock, on the whole sale's stocked lines, only when the
          // cashier says so: a returned jar is sometimes back on the shelf and
          // sometimes in the bin (schema `refund_lines`).
          ...(restock
            ? {
                lines: data.lines
                  .filter((line) => line.item_id !== null)
                  .map((line) => ({
                    settlement_line_id: line.line_id,
                    amount_cents: Math.max(0, line.charged_cents - line.refunded_cents),
                    restock: true,
                  }))
                  .filter((line) => line.amount_cents > 0),
              }
            : {}),
        },
        schema: Refund,
      });
      onDone(refund);
    } catch (e) {
      // The server's own sentence, printed as it came — the till and the ledger
      // must not disagree about why something was refused.
      setError(e instanceof Error ? e.message : t('refund.failed'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="sheet" role="dialog" aria-label={t('refund.dialogLabel')}>
      <div className="sheet__panel">
        <h2>{t('refund.title')}</h2>

        {data === null ? (
          <p className="muted">
            {connection === 'live' ? t('refund.loading') : t('refund.needsConnection')}
          </p>
        ) : (
          <>
            <p className="muted">
              {t('refund.sale', {
                date: data.settled_on,
                total: money(data.total_cents),
                left: money(data.refundable_cents),
              })}
            </p>

            <label className="field">
              <span>{t('refund.amount')}</span>
              <input
                aria-label={t('refund.amount')}
                inputMode="decimal"
                placeholder={(data.refundable_cents / 100).toFixed(2)}
                value={amountText}
                onChange={(e) => setAmountText(e.target.value)}
              />
              <small>{t('refund.amountHint')}</small>
            </label>

            {/*
              A tip is capped on its own, because it was never revenue and
              refunding it reduces none (schema `refunds`). The field appears
              only when there is a tip to give back.
            */}
            {data.refundable_tip_cents > 0 ? (
              <label className="field">
                <span>{t('refund.tip', { left: money(data.refundable_tip_cents) })}</span>
                <input
                  aria-label={t('refund.tip', { left: money(data.refundable_tip_cents) })}
                  inputMode="decimal"
                  placeholder="0.00"
                  value={tipText}
                  onChange={(e) => setTipText(e.target.value)}
                />
              </label>
            ) : null}

            <div className="field">
              <span>{t('refund.howBack')}</span>
              <div className="tender-buttons" role="radiogroup" aria-label={t('refund.howBack')}>
                {REFUND_TENDERS.map((kind) => (
                  <button
                    key={kind}
                    type="button"
                    role="radio"
                    aria-checked={tender === kind}
                    className={`tender-button${tender === kind ? ' is-chosen' : ''}`}
                    onClick={() => setTender(kind)}
                  >
                    {t(`refund.tender.${kind}`)}
                  </button>
                ))}
              </div>
              <small>{t('refund.recordedOnly')}</small>
              {tender === 'other' ? (
                <input
                  aria-label={t('refund.labelLabel')}
                  placeholder={t('refund.labelPlaceholder')}
                  maxLength={40}
                  value={label}
                  onChange={(e) => setLabel(e.target.value)}
                />
              ) : null}
            </div>

            <label className="field field--row">
              <span>{t('refund.restock')}</span>
              <input
                type="checkbox"
                aria-label={t('refund.restock')}
                checked={restock}
                onChange={(e) => setRestock(e.target.checked)}
              />
              <small>{t('refund.restockHint')}</small>
            </label>

            <label className="field">
              <span>{t('refund.reason')}</span>
              <input value={reason} onChange={(e) => setReason(e.target.value)} maxLength={200} />
            </label>

            {tooMuch ? <p className="error">{t('refund.tooMuch')}</p> : null}
          </>
        )}

        {error !== null ? <p className="error">{error}</p> : null}

        <div className="sheet__actions">
          <button type="button" className="button button--ghost" onClick={onCancel}>
            {t('common.cancel')}
          </button>
          <button
            type="button"
            className="button button--primary"
            disabled={data === null || busy || tooMuch || amountCents + tipCents <= 0}
            onClick={() => void submit()}
          >
            {busy ? t('refund.giving') : t('refund.give')}
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * What a purchase says about the money that came back off it.
 *
 * A line rather than a strike-through on the sale, for the schema's own reason:
 * the settlement is what it was, and a screen that rewrote it would be teaching
 * the owner that a ledger is editable.
 */
export function RefundedNote({ cents, currency }: { cents: number; currency: string }): ReactNode {
  const t = useT();
  const locale = useLocale();
  if (cents <= 0) return null;
  return (
    <small className="muted">
      {t('refund.alreadyBack', { amount: formatMoney(cents, currency, locale) })}
    </small>
  );
}
